// Query options and hooks for fetching projects from legacy Aspirex system

import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { trpcClient } from "../trpc";

export const projectsKeys = {
  all: ["projects"] as const,
  listByOrg: (organizationId: string) =>
    [...projectsKeys.all, "list", organizationId] as const,
  applicationsByOrg: (organizationId: string) =>
    [...projectsKeys.all, "applications", organizationId] as const,
  resolveByProgramId: (programId: number, embeddedUrl?: string) =>
    [
      ...projectsKeys.all,
      "resolveByProgramId",
      programId,
      embeddedUrl,
    ] as const,
  resolveByMemberId: (memberId: number, secureUrl?: string) =>
    [...projectsKeys.all, "resolveByMemberId", memberId, secureUrl] as const,
  interactions: (
    organizationId: string,
    programId: number,
    profileId: string,
  ) =>
    [
      ...projectsKeys.all,
      "interactions",
      organizationId,
      programId,
      profileId,
    ] as const,
  accessRequests: (organizationId: string) =>
    [...projectsKeys.all, "accessRequests", organizationId] as const,
  clientLogos: (organizationId: string, clientIds: readonly string[]) =>
    [...projectsKeys.all, "clientLogos", organizationId, clientIds] as const,
};

// Types inferred from API response
type ProjectsListResult = Awaited<
  ReturnType<typeof trpcClient.projects.list.query>
>;

export type Project = ProjectsListResult["projects"][number];
export type OfferClient = ProjectsListResult["offerClients"][number];

type ApplicationsResult = Awaited<
  ReturnType<typeof trpcClient.projects.applications.query>
>;

/** A pending application, shaped like a project (EPD-12314). */
export type PendingApplication = ApplicationsResult["applications"][number];

export type ResolvedProgram = Awaited<
  ReturnType<typeof trpcClient.projects.resolveByProgramId.query>
>;

type InteractionsResult = Awaited<
  ReturnType<typeof trpcClient.projects.interactions.query>
>;

export type CreatorInteraction = InteractionsResult["interactions"][number];

// Query Options

export function projectsListQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: projectsKeys.listByOrg(organizationId),
    queryFn: () => trpcClient.projects.list.query({ organizationId }),
    // EPD-6938: gate the request when organizationId is missing so callsites
    // can pass a safe fallback (e.g. organization?.id ?? "") and keep hook
    // call order stable while the org-context guard renders null.
    enabled: organizationId.length > 0,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/** Pending applications across the org's profiles, for Campaigns → Applied (EPD-12314). */
export function projectApplicationsQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: projectsKeys.applicationsByOrg(organizationId),
    queryFn: () => trpcClient.projects.applications.query({ organizationId }),
    enabled: organizationId.length > 0,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * How long a logo answer stays fresh. Logos change rarely, so an answer with
 * every brand is kept for the session. AspireX answers a backend_server outage
 * with no logos rather than an error, so an answer missing brands is asked
 * again after a minute.
 */
export function clientLogosStaleTime(
  clientIds: readonly string[],
  logos: Readonly<Record<string, string | null>> | undefined,
): number {
  const complete = !!logos && clientIds.every((id) => id in logos);
  return complete ? 60 * 60_000 : 60_000;
}

/**
 * Create admin brand logos by client id, for the Campaigns and Offers pages
 * (EPD-12612). Pass the ids sorted so the cache key is stable.
 */
export function clientLogosQueryOptions(
  organizationId: string,
  clientIds: readonly string[],
) {
  return queryOptions({
    queryKey: projectsKeys.clientLogos(organizationId, clientIds),
    queryFn: () =>
      trpcClient.projects.clientLogos.query({
        organizationId,
        clientIds: [...clientIds],
      }),
    enabled: organizationId.length > 0 && clientIds.length > 0,
    // The page's brands can grow while it loads (applications land on their
    // own); keep the logos already found instead of blanking the avatars.
    placeholderData: keepPreviousData,
    staleTime: (query) =>
      clientLogosStaleTime(clientIds, query.state.data?.logos),
    gcTime: 60 * 60_000,
    // A cosmetic lookup: one retry, so a slow AspireX isn't asked four times.
    retry: 1,
  });
}

export function resolveByProgramIdQueryOptions(
  programId: number,
  embeddedUrl?: string,
) {
  return queryOptions({
    queryKey: projectsKeys.resolveByProgramId(programId, embeddedUrl),
    queryFn: () =>
      trpcClient.projects.resolveByProgramId.query({
        programId,
        embeddedUrl,
      }),
    // Retry once for transient/network errors, but not for deterministic client errors:
    // NOT_FOUND = campaign doesn't exist, FORBIDDEN = email mismatch, BAD_REQUEST = invalid input
    retry: (failureCount, error) => {
      if (failureCount >= 1) return false;
      if (error instanceof TRPCClientError) {
        const code = (error.data as { code?: string } | undefined)?.code;
        if (
          code === "NOT_FOUND" ||
          code === "FORBIDDEN" ||
          code === "BAD_REQUEST"
        ) {
          return false;
        }
      }
      return true;
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

export function resolveByMemberIdQueryOptions(memberId: number) {
  return queryOptions({
    queryKey: projectsKeys.resolveByMemberId(memberId),
    queryFn: () => trpcClient.projects.resolveByMemberId.query({ memberId }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

export function projectInteractionsQueryOptions(
  organizationId: string,
  programId: number,
  profileId: string,
) {
  return queryOptions({
    queryKey: projectsKeys.interactions(organizationId, programId, profileId),
    queryFn: (): Promise<InteractionsResult> =>
      trpcClient.projects.interactions.query({
        organizationId,
        programId,
        profileId,
      }),
    // EPD-6938: mirror the projectsListQueryOptions gate so any caller passing
    // an empty organizationId (e.g. the `organization?.id ?? ""` fallback in
    // routes/(org)/o.$orgSlug/projects/index.tsx when org context is null)
    // skips the network request rather than firing it with an empty arg.
    enabled: organizationId.length > 0,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

// Query Hooks

export function useProjectsListQuery(organizationId: string) {
  return useQuery(projectsListQueryOptions(organizationId));
}

export function useProjectApplicationsQuery(organizationId: string) {
  return useQuery(projectApplicationsQueryOptions(organizationId));
}

export function useResolveByProgramIdQuery(
  programId: number,
  options?: { enabled?: boolean; embeddedUrl?: string },
) {
  return useQuery({
    ...resolveByProgramIdQueryOptions(programId, options?.embeddedUrl),
    enabled: options?.enabled ?? true,
  });
}

export function useResolveByMemberIdQuery(
  memberId: number | undefined,
  options?: { enabled?: boolean; secureUrl?: string },
) {
  return useQuery({
    queryKey: projectsKeys.resolveByMemberId(memberId ?? 0, options?.secureUrl),
    queryFn: () =>
      trpcClient.projects.resolveByMemberId.query({
        memberId: memberId ?? 0,
        secureUrl: options?.secureUrl,
      }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled: (options?.enabled ?? true) && memberId !== undefined,
  });
}

export function useRequestAccessMutation() {
  return useMutation({
    mutationFn: (data: { programId: number; embeddedUrl?: string }) =>
      trpcClient.projects.requestAccess.mutate(data),
  });
}

export function useApproveAccessRequestMutation() {
  return useMutation({
    mutationFn: (data: { token: string }) =>
      trpcClient.projects.approveAccessRequest.mutate(data),
  });
}

export function useRejectAccessRequestMutation() {
  return useMutation({
    mutationFn: (data: { token: string }) =>
      trpcClient.projects.rejectAccessRequest.mutate(data),
  });
}

// Types inferred from listAccessRequests API response
type AccessRequestsResult = Awaited<
  ReturnType<typeof trpcClient.projects.listAccessRequests.query>
>;
export type AccessRequest = AccessRequestsResult["requests"][number];

export function useListAccessRequestsQuery(organizationId: string) {
  return useQuery({
    queryKey: projectsKeys.accessRequests(organizationId),
    queryFn: () =>
      trpcClient.projects.listAccessRequests.query({ organizationId }),
  });
}

export function useApproveAccessRequestAsAdminMutation(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accessRequestId: string) =>
      trpcClient.projects.approveAccessRequestAsAdmin.mutate({
        organizationId,
        accessRequestId,
      }),
    onMutate: async (accessRequestId: string) => {
      await queryClient.cancelQueries({
        queryKey: projectsKeys.accessRequests(organizationId),
      });
      const snapshot = queryClient.getQueryData(
        projectsKeys.accessRequests(organizationId),
      );
      queryClient.setQueryData(
        projectsKeys.accessRequests(organizationId),
        (old: AccessRequestsResult | undefined) =>
          old
            ? {
                ...old,
                requests: old.requests.filter((r) => r.id !== accessRequestId),
              }
            : old,
      );
      return { snapshot };
    },
    onError: (_err, _accessRequestId, context) => {
      if (context?.snapshot !== undefined) {
        queryClient.setQueryData(
          projectsKeys.accessRequests(organizationId),
          context.snapshot,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectsKeys.accessRequests(organizationId),
      });
    },
  });
}

export function useRejectAccessRequestAsAdminMutation(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accessRequestId: string) =>
      trpcClient.projects.rejectAccessRequestAsAdmin.mutate({
        organizationId,
        accessRequestId,
      }),
    onMutate: async (accessRequestId: string) => {
      await queryClient.cancelQueries({
        queryKey: projectsKeys.accessRequests(organizationId),
      });
      const snapshot = queryClient.getQueryData(
        projectsKeys.accessRequests(organizationId),
      );
      queryClient.setQueryData(
        projectsKeys.accessRequests(organizationId),
        (old: AccessRequestsResult | undefined) =>
          old
            ? {
                ...old,
                requests: old.requests.filter((r) => r.id !== accessRequestId),
              }
            : old,
      );
      return { snapshot };
    },
    onError: (_err, _accessRequestId, context) => {
      if (context?.snapshot !== undefined) {
        queryClient.setQueryData(
          projectsKeys.accessRequests(organizationId),
          context.snapshot,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectsKeys.accessRequests(organizationId),
      });
    },
  });
}

export function useProjectInteractionsQuery(
  organizationId: string,
  programId: number,
  profileId: string,
  options?: { enabled?: boolean },
): UseQueryResult<InteractionsResult> {
  return useQuery({
    ...projectInteractionsQueryOptions(organizationId, programId, profileId),
    // EPD-6938: AND with the empty-orgId guard from projectInteractionsQueryOptions
    // — a bare `options?.enabled ?? true` would overwrite the spread `enabled`
    // and let an empty organizationId reach the network.
    enabled: organizationId.length > 0 && (options?.enabled ?? true),
  });
}
